/**
 * Advanced Analytics — map an alert's curated "related signals" onto entity
 * nodes in the digital-twin hierarchy tree, then produce dot-paths that
 * `extractEntities` can resolve into chart series.
 *
 * Each related signal carries an `entityType` (battery / string / pack / module
 * / cell / tms / vehicle). Relative to the alert's affected entity we place it:
 *   - same entity type      -> the affected node itself
 *   - ancestor (up-tree)     -> the affected node's ancestor of that type
 *   - descendant (down-tree) -> EVERY individual node of that type under the
 *                               affected node (e.g. affected pack + cell signal
 *                               => all cells of all modules under that pack)
 *   - cross base-entity tree -> when the affected entity is battery/string and
 *                               the signal is tms/vehicle, the tms / bus entity
 *                               of the same system
 * Any combination not covered above is ignored (skipped).
 *
 * The path segments are node NAMES as produced by `transformTree`
 * (cleanName(label)), and the final segment is the metric name
 * (cleanName(label, "_")). `extractEntities` matches children by these names.
 */

// Battery sub-tree ordered from top (battery) to bottom (cell).
export const BATTERY_TREE_ORDER = ['battery', 'string', 'pack', 'module', 'cell'];

/**
 * Map a signal's backend entityType to the node "type" that
 * `extractEntities`/inferNodeType derives from a node name. The vehicle entity
 * is labelled "Bus" in the hierarchy, so its node type is "bus".
 */
export const signalTypeToNodeType = (entityType) =>
    entityType === 'vehicle' ? 'bus' : entityType;

/** Metric leaf key: lowercase, spaces -> underscores (mirrors transformTree). */
export const cleanMetricName = (label) =>
    String(label || '').trim().toLowerCase().replace(/\s+/g, '_');

/**
 * Infer a node's entity type from its (cleaned) name, mirroring
 * extractEntities' inferTypeFromName: strip trailing digits and normalize
 * "batterysystem" -> "battery".
 */
export const inferNodeType = (name) => {
    const base = String(name || '').replace(/\s+/g, '').replace(/\d+$/g, '').toLowerCase();
    return base === 'batterysystem' ? 'battery' : base;
};

/** Locate a node by id; returns { path: string[], node } or null. */
const findNodePathAndNode = (hierarchy, targetId) => {
    if (targetId == null) return null;
    const target = String(targetId);
    const traverse = (node, path) => {
        if (node.id != null && String(node.id) === target) return { path, node };
        if (node.children) {
            for (const [key, child] of Object.entries(node.children)) {
                if (child.type === 'complex') {
                    const r = traverse(child, [...path, key]);
                    if (r) return r;
                }
            }
        }
        return null;
    };
    for (const [rootKey, rootNode] of Object.entries(hierarchy)) {
        const r = traverse(rootNode, [rootKey]);
        if (r) return r;
    }
    return null;
};

/** Collect name-paths of every individual complex node of `nodeType` under `startNode`. */
const collectDescendantPaths = (startNode, startPath, nodeType) => {
    const out = [];
    const traverse = (node, path) => {
        if (!node.children) return;
        for (const [key, child] of Object.entries(node.children)) {
            if (child.type !== 'complex') continue;
            const childPath = [...path, key];
            // Skip aggregate "<level>all" nodes so we plot individual entities.
            if (inferNodeType(child.name) === nodeType && !/all$/.test(key)) {
                out.push(childPath);
            }
            traverse(child, childPath);
        }
    };
    traverse(startNode, startPath);
    return out;
};

/** Root nodes whose type matches (cross-tree targets: tms / bus). */
const findRootPathsByType = (hierarchy, nodeType) => {
    const out = [];
    for (const [rootKey, rootNode] of Object.entries(hierarchy)) {
        if (inferNodeType(rootNode.name) === nodeType) out.push([rootKey]);
    }
    return out;
};

/**
 * Build the dot-paths for the related signals.
 * @param {object} args
 * @param {object[]} args.relatedSignals - [{ entityType, label, key, ... }]
 * @param {string} args.entityAffectedType
 * @param {string|number} args.entityAffectedId
 * @param {object} args.hierarchy - transformTree output (map of roots)
 * @returns {string[]} resolvable dot-paths
 */
export const buildRelatedSignalPaths = ({
    relatedSignals,
    entityAffectedType,
    entityAffectedId,
    hierarchy,
}) => {
    if (!Array.isArray(relatedSignals) || relatedSignals.length === 0) return [];
    if (!hierarchy || Object.keys(hierarchy).length === 0) return [];

    const affectedType = entityAffectedType || 'battery';
    const found = findNodePathAndNode(hierarchy, entityAffectedId);
    // Fall back to the battery-system root when the node cannot be located.
    const affectedPath = found ? found.path : ['batterysystem'];
    const affectedNode =
        found ? found.node : (hierarchy['batterysystem'] || Object.values(hierarchy)[0]);

    const paths = [];
    const seen = new Set();
    const addPath = (segments, metricName) => {
        const p = [...segments, metricName].join('.');
        if (!seen.has(p)) {
            seen.add(p);
            paths.push(p);
        }
    };

    for (const signal of relatedSignals) {
        if (!signal || !signal.entityType || !signal.label) continue;
        const metricName = cleanMetricName(signal.label);
        const sigType = signal.entityType;

        // Same entity type -> the affected node itself.
        if (sigType === affectedType) {
            addPath(affectedPath, metricName);
            continue;
        }

        const affIdx = BATTERY_TREE_ORDER.indexOf(affectedType);
        const sigIdx = BATTERY_TREE_ORDER.indexOf(sigType);

        // Both within the battery sub-tree -> ancestor or descendant.
        if (affIdx !== -1 && sigIdx !== -1) {
            const targetNodeType = signalTypeToNodeType(sigType);
            if (sigIdx < affIdx) {
                // Ancestor: truncate the affected path to that ancestor.
                let cut = -1;
                for (let i = affectedPath.length - 1; i >= 0; i--) {
                    if (inferNodeType(affectedPath[i]) === targetNodeType) {
                        cut = i;
                        break;
                    }
                }
                if (cut !== -1) addPath(affectedPath.slice(0, cut + 1), metricName);
            } else {
                // Descendant: every individual node of that type under affected.
                collectDescendantPaths(affectedNode, affectedPath, targetNodeType)
                    .forEach((dp) => addPath(dp, metricName));
            }
            continue;
        }

        // Cross base-entity tree: battery/string affected + tms/vehicle signal
        // -> the tms / bus entity of the same system.
        if (
            (affectedType === 'battery' || affectedType === 'string') &&
            (sigType === 'tms' || sigType === 'vehicle')
        ) {
            const targetNodeType = signalTypeToNodeType(sigType);
            findRootPathsByType(hierarchy, targetNodeType).forEach((rp) =>
                addPath(rp, metricName)
            );
        }
        // Otherwise: unsupported combination -> skip.
    }

    return paths;
};
